import { onScopeDispose } from 'vue';
import DisableDevtool from 'disable-devtool';

// 说明:禁调试委托给成熟库 disable-devtool(MIT / 零依赖)。
// 它默认启用 console 渲染陷阱类检测器(RegToString / DefineId / DateToString /
// FuncToString 等),在 DevTools 渲染被 log 对象时触发,因此:
//  - 覆盖 F12、右键、以及浏览器菜单(⋮ → 更多工具 → 开发者工具)所有入口;
//  - 不依赖窗口尺寸,避免全屏 / 地址栏隐藏 / 缩放导致的误报。
// 检测器枚举:0=RegToString 1=DefineId 2=Size 3=DateToString 4=FuncToString
//            5=Debugger 6=Performance 7=DebugLib。显式排除 2(Size),彻底杜绝尺寸误报。
const DETECTORS = [0, 1, 3, 4, 5, 6, 7];

// 检测到 DevTools 打开时跳回工作台首页。
const REDIRECT_URL = `https://${import.meta.env.VITE_WEB_DOMAIN}`;

// 正式环境调试白名单:cookie 名刻意非语义化;源码只存 token 的 MD5,明文不入库。
// 明文由团队私发;校验失败或值被随意注入时不会放行。
const BYPASS_COOKIE_NAME = '_pb_x9k2';
const BYPASS_TOKEN_MD5 = '712c8d5b9f9baf706ffa499ecb18d25f';

// disable-devtool 是页面级单例,只能初始化一次;之后用 isSuspend 动态挂起/恢复。
let initialized = false;

function ensureInitialized() {
  if (initialized) return;
  initialized = true;
  DisableDevtool({
    detectors: DETECTORS,
    disableMenu: true, // 禁用右键菜单
    url: REDIRECT_URL, // 检测到打开 → 跳转回工作台
    clearIntervalWhenDevOpenTrigger: true, // 触发跳转后停止监控,避免重复触发
    // 检测循环中再次读 cookie,设置后无需依赖重建 composable
    ignore: hasDevtoolsBypassCookie
  });
  // 初始化即运行;由各使用方通过 isSuspend 决定是否真正生效。
  DisableDevtool.isSuspend = true;
}

/** 按名称读取单个 cookie 值。 */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const item = part.trim();
    if (item.startsWith(prefix)) {
      return decodeURIComponent(item.slice(prefix.length));
    }
  }
  return null;
}

/** 校验白名单 cookie:值经 MD5 后与摘要一致才放行。 */
function hasDevtoolsBypassCookie(): boolean {
  const token = readCookie(BYPASS_COOKIE_NAME);
  if (!token) return false;
  return DisableDevtool.md5(token) === BYPASS_TOKEN_MD5;
}

/** 当前 MODE 是否落在需要禁调试的环境列表内。 */
function isGuardedMode(modes: AppMode[]): boolean {
  return modes.includes(import.meta.env.MODE as AppMode);
}

// 引用计数:阅读端整页与预览弹窗可能先后启用,归零才挂起。
let activeCount = 0;

/** 项目构建环境。 */
export type AppMode = 'dev' | 'test' | 'pro';

export type UseDebugGuardOptions = {
  /** 在这些环境下启用禁调试；默认 ['pro']，test dev 可正常调试。 */
  modes?: AppMode[];
};

/**
 * 禁用调试的 composable:委托 disable-devtool 拦截右键 / F12 / 浏览器菜单打开的
 * DevTools,并在检测到打开时跳转回工作台(VITE_WEB_DOMAIN)。
 * - 通过 modes 指定哪些环境启用；当前 MODE 不在列表内则为 no-op。
 * - 本地 cookie `_pb_x9k2` 的值 MD5 命中白名单摘要时跳过启用。
 * - 引用计数 + isSuspend:0→1 恢复检测,1→0 挂起检测(不污染工作台)。
 * - onScopeDispose 在宿主组件卸载时兜底 disable,防止计数残留。
 */
export function useDebugGuard(options?: UseDebugGuardOptions) {
  const modes = options?.modes ?? ['pro'];
  // 每个使用者是否已计入,防止同一宿主重复 enable 导致计数虚高。
  let enabledHere = false;

  const enable = () => {
    // 步骤1:环境不在守卫列表,或已有白名单 cookie → 直接跳过
    if (!isGuardedMode(modes) || hasDevtoolsBypassCookie() || enabledHere) return;
    // 步骤2:计入引用并初始化检测库
    enabledHere = true;
    ensureInitialized();
    // 步骤3:首个启用者恢复检测
    if (++activeCount === 1) DisableDevtool.isSuspend = false;
  };

  const disable = () => {
    if (!enabledHere) return;
    enabledHere = false;
    if (--activeCount === 0) DisableDevtool.isSuspend = true;
  };

  onScopeDispose(disable);

  return { enable, disable };
}
